/**
 * Bandeau KPI avec bascule « Grand public / Expert » (PRD §2). Le point « année de base » est construit depuis la
 * baseline (avec sa source) ; les années suivantes viennent de la Timeline (valeurs simulées).
 */
import type { Baseline, Figure, YearState } from "../../../engine/src/types.js";
import { escapeHtml as e, eur, int, millions, missionShort, num, pct, signed } from "../format.js";
import type { Store } from "../state.js";
import { figureNotes, figureSources, srcButton } from "./source-info.js";
import { majorityGauge, majorityText, majorityThreshold, medianIncomeMonthly, paramsSourceButton, ppEuros } from "./tangible.js";

type Block = "macro" | "apu" | "politics" | "trade";

export interface KpiDef {
  key: string;
  label: string;
  unit: "%" | "";
  dec: number;
  /** true si une hausse est défavorable (dette, chômage, pauvreté). */
  invert: boolean;
  baseline: [Block, string];
}

export const KPI_DEFS: KpiDef[] = [
  { key: "deficit_pct_gdp", label: "Déficit public / PIB", unit: "%", dec: 1, invert: false, baseline: ["apu", "deficit_pct_gdp"] },
  { key: "debt_pct_gdp", label: "Dette / PIB", unit: "%", dec: 0, invert: true, baseline: ["apu", "debt_pct_gdp"] },
  { key: "growth_pct", label: "Croissance", unit: "%", dec: 1, invert: false, baseline: ["macro", "gdp_growth"] },
  { key: "unemployment_pct", label: "Chômage", unit: "%", dec: 1, invert: true, baseline: ["macro", "unemployment_rate"] },
  { key: "poverty_pct", label: "Pauvreté", unit: "%", dec: 1, invert: true, baseline: ["macro", "poverty_rate"] },
  { key: "confidence", label: "Confiance des marchés", unit: "", dec: 0, invert: false, baseline: ["politics", "confidence"] },
  { key: "cohesion", label: "Cohésion sociale", unit: "", dec: 0, invert: false, baseline: ["politics", "cohesion"] },
  { key: "political_capital", label: "Capital politique", unit: "", dec: 0, invert: false, baseline: ["politics", "political_capital"] },
];

/** Autres clés KPI lues par la vue grand public et le graphique (avec leur figure de baseline). */
const EXTRA_BASELINE: Record<string, [Block, string]> = {
  interest_bn: ["apu", "interest_bn"],
  debt_bn: ["apu", "debt_bn"],
  marginal_rate_pct: ["apu", "marginal_rate_oat10"],
  co2_mt: ["macro", "ghg_emissions"],
  trade_balance_bn: ["trade", "balance_bn"],
  eu_relation: ["politics", "eu_relation"],
  gdp_bn: ["macro", "gdp_nominal"],
};

export function baselineFigure(b: Baseline, key: string): Figure | undefined {
  const def = KPI_DEFS.find((d) => d.key === key)?.baseline ?? EXTRA_BASELINE[key];
  if (!def) return undefined;
  return b[def[0]][def[1]];
}

/** Pseudo-YearState de l'année de base, uniquement pour afficher le point de départ. */
export function baselinePoint(b: Baseline): YearState {
  const kpis: Record<string, number> = {};
  for (const key of [...KPI_DEFS.map((d) => d.key), ...Object.keys(EXTRA_BASELINE)]) {
    const v = baselineFigure(b, key)?.value;
    if (v !== null && v !== undefined) kpis[key] = v;
  }
  const service_index: Record<string, number> = {};
  for (const m of b.state.missions) service_index[m.id] = m.service_index?.value ?? 1;
  return { year: b.meta.base_year, kpis, service_index, account: { resources: [], expenses: [], balance_state: 0, balance_asso: 0, balance_apul: 0, balance_apu: 0 }, events_fired: [], journal: [] };
}

function kpiSource(store: Store, key: string, label: string, isBase: boolean) {
  const fig = baselineFigure(store.content.baseline, key);
  const notes = figureNotes(fig);
  const def = KPI_DEFS.find((d) => d.key === key)?.baseline ?? EXTRA_BASELINE[key];
  if (def && store.content.unsourcedPaths.includes(`${def[0]}.${def[1]}`)) notes.unshift("Non sourcé : chiffre de la baseline de secours (prototype), la baseline réelle ne le fournit pas encore (docs/sources-todo.md).");
  if (!isBase) notes.push(`Valeur de l'année simulée : calculée par ${store.engine.name} à partir de la baseline et des paramètres.`);
  return srcButton({ title: label, sources: figureSources(fig), status: isBase ? undefined : "simulated", notes });
}

function delta(cur: number | undefined, prev: number | undefined, dec: number, invert: boolean): string {
  if (cur === undefined || prev === undefined) return `<div class="d flat">—</div>`;
  const d = cur - prev;
  const eps = Math.pow(10, -dec) / 2;
  const good = invert ? -d : d;
  const cls = good > eps ? "up" : good < -eps ? "down" : "flat";
  return `<div class="d ${cls}">${signed(d, dec)}</div>`;
}

/** KPIs v0.2 en vue expert (affichés seulement si le moteur les fournit). */
const V02_EXPERT: Array<{ key: string; label: string; dec: number; invert: boolean }> = [
  { key: "parliament_majority", label: "Majorité (% des sièges)", dec: 0, invert: false },
  { key: "purchasing_power_index", label: "Pouvoir d'achat (indice)", dec: 1, invert: false },
];

export function expertRow(store: Store, cur: YearState, prev: YearState | undefined, isBase: boolean): string {
  const extra = V02_EXPERT.filter((d) => cur.kpis[d.key] !== undefined)
    .map((d) => {
      const v = cur.kpis[d.key]!;
      return `<div class="kpi">${paramsSourceButton(store, d.label, d.key === "parliament_majority" ? "majority" : "purchasing", [], false)}<div class="l">${e(d.label)}</div><div class="v">${num(v, d.dec)}</div>${delta(v, prev?.kpis[d.key], d.dec, d.invert)}</div>`;
    })
    .join("");
  return `<div class="kpis">${KPI_DEFS.map((d) => {
    const v = cur.kpis[d.key];
    const shown = v === undefined ? "—" : d.unit === "%" ? pct(v, d.dec) : num(v, d.dec);
    return `<div class="kpi">${kpiSource(store, d.key, d.label, isBase)}<div class="l">${e(d.label)}</div><div class="v">${shown}</div>${delta(v, prev?.kpis[d.key], d.dec, d.invert)}</div>`;
  }).join("")}${extra}</div>`;
}

function card(store: Store, key: string, label: string, value: string, explain: string, isBase: boolean): string {
  return `<div class="kpi">${kpiSource(store, key, label, isBase)}<div class="l">${e(label)}</div><div class="v">${value}</div><div class="e">${explain}</div></div>`;
}

function paramVal(store: Store, id: string): number | undefined {
  const v = store.content.params[id]?.value;
  return v === null ? undefined : v;
}
function missionCredits(b: Baseline, id: string): number | undefined {
  const v = b.state.missions.find((m) => m.id === id)?.credits.value;
  return v === null ? undefined : v;
}

/**
 * Services publics : indice moyens / besoins de chaque mission, en % des besoins couverts, comparé au départ
 * (remplace les étoiles v0.1 dont l'échelle `stars_*` mettait tout à zéro sous 0,85 — red team).
 */
export function servicesCard(store: Store, cur: YearState, isBase: boolean): string {
  const b = store.content.baseline;
  const wanted = ["enseignement_scolaire", "sante", "securites_justice", "securites"];
  const shown = wanted.filter((id) => cur.service_index[id] !== undefined);
  const ids = shown.length ? shown : Object.keys(cur.service_index).slice(0, 3);
  if (!ids.length) return "";
  const lines = ids
    .map((id) => {
      const m = b.state.missions.find((x) => x.id === id);
      const v = cur.service_index[id]!;
      const v0 = m?.service_index?.value ?? undefined;
      const d = v0 !== undefined ? v - v0 : 0;
      const cls = Math.abs(d) < 0.005 ? "" : d > 0 ? "ok" : "bad";
      const w = Math.max(0, Math.min(100, v * 100));
      return `<div class="svc"><span>${e(missionShort(m?.label ?? id))}</span><span class="svcbar"><i class="${cls}" style="width:${w.toFixed(0)}%"></i></span><b class="${cls === "ok" ? "up" : cls === "bad" ? "down" : ""}">${int(v * 100)} %</b></div>`;
    })
    .join("");
  return `<div class="kpi">${srcButton({ title: "Services publics", sources: b.state.missions.filter((m) => ids.includes(m.id)).map((m) => m.service_index?.source ?? "TODO"), status: isBase ? undefined : "simulated", notes: ["Indice composite moyens / besoins par mission : 100 % = besoin couvert au niveau de départ. Le rabot et les mesures de réduction d'effectifs le font baisser chaque année (paramètres cut_*)."] })}<div class="l">Services publics (besoins couverts)</div><div class="v svcs">${lines}</div></div>`;
}

/** Carte « Majorité au Parlement » (si le moteur fournit `parliament_majority`). */
export function majorityCard(store: Store, cur: YearState, isBase: boolean): string {
  const v = cur.kpis.parliament_majority;
  if (v === undefined) return "";
  const t = majorityThreshold(store);
  return `<div class="kpi">${paramsSourceButton(store, "Majorité au Parlement", "majority", isBase ? [] : ["Valeur simulée : perdue par les mesures impopulaires, les crises mal gérées et la baisse du pouvoir d'achat."], false)}<div class="l">Majorité au Parlement</div>${majorityGauge(v, t, "s")}<div class="e">${e(majorityText(v, t))}</div></div>`;
}

/** Carte « Pouvoir d'achat » (si le moteur fournit `purchasing_power_index`). */
export function purchasingCard(store: Store, cur: YearState): string {
  const v = cur.kpis.purchasing_power_index;
  if (v === undefined) return "";
  const b = store.content.baseline;
  const inc = medianIncomeMonthly(b);
  const euros = ppEuros(b, v);
  const extra = inc ? [`Revenu médian de référence : ${eur(inc.value)} par mois${inc.fig.source === "TODO" ? " (non sourcé)" : ""}.`] : ["Revenu médian des ménages absent de la baseline : seul l'indice est affiché."];
  return `<div class="kpi">${paramsSourceButton(store, "Pouvoir d'achat", "purchasing", extra, false)}<div class="l">Pouvoir d'achat (100 = ${b.meta.base_year})</div><div class="v">${num(v, 1)}</div><div class="e">${euros || `${signed(v - 100, 1)} pt depuis ${b.meta.base_year}`}</div></div>`;
}

export function publicRow(store: Store, cur: YearState, yearOffset: number, isBase: boolean): string {
  const b = store.content.baseline;
  const k = cur.kpis;
  const cards: string[] = [];
  const maj = majorityCard(store, cur, isBase);
  if (maj) cards.push(maj);
  const pp = purchasingCard(store, cur);
  if (pp) cards.push(pp);
  const rule = paramVal(store, "deficit_rule_pct_gdp");
  if (k.deficit_pct_gdp !== undefined) {
    const d = k.deficit_pct_gdp;
    const ruleTxt = rule !== undefined ? `Règle européenne : ${pct(rule, 0)} max. ${d > -rule ? "Dans les clous." : "Au-dessus de la limite."}` : "";
    cards.push(card(store, "deficit_pct_gdp", "Ce que les administrations dépensent de plus qu'elles ne gagnent", `${d < 0 ? "−" : "+"}${pct(Math.abs(d))} du PIB`, ruleTxt, isBase));
  }
  const population = b.macro.population?.value ?? undefined;
  const medSal = b.macro.median_net_salary_monthly?.value ?? undefined;
  if (k.debt_bn !== undefined && population) {
    const perHead = (k.debt_bn * 1e9) / population;
    const months = medSal ? `≈ ${int(perHead / medSal)} mois de salaire médian · ` : "";
    cards.push(card(store, "debt_bn", "Dette par habitant", eur(Math.round(perHead / 100) * 100), `${months}${k.debt_pct_gdp !== undefined ? pct(k.debt_pct_gdp, 0) + " du PIB" : ""}`, isBase));
  }
  const justice = missionCredits(b, "justice");
  if (k.interest_bn !== undefined) {
    cards.push(card(store, "interest_bn", "Intérêts de la dette", `${int(k.interest_bn)} Md€/an`, justice ? `≈ ${num(k.interest_bn / justice, 1)} fois le budget de la Justice` : "", isBase));
  }
  const labourForce = b.macro.labour_force?.value ?? (b.macro.unemployed_persons?.value && b.macro.unemployment_rate?.value ? (b.macro.unemployed_persons.value / b.macro.unemployment_rate.value) * 100 : undefined);
  if (k.unemployment_pct !== undefined) {
    cards.push(card(store, "unemployment_pct", "Chômage", pct(k.unemployment_pct), labourForce ? `≈ ${millions((k.unemployment_pct / 100) * labourForce)} de personnes` : "", isBase));
  }
  if (k.poverty_pct !== undefined && k.poverty_pct > 0) {
    cards.push(card(store, "poverty_pct", "Pauvreté", `1 Français sur ${int(100 / k.poverty_pct)}`, `${pct(k.poverty_pct)} sous le seuil`, isBase));
  }
  const lo = paramVal(store, "confidence_low");
  const hi = paramVal(store, "confidence_high");
  if (k.confidence !== undefined && lo !== undefined && hi !== undefined) {
    const c = k.confidence;
    const lvl = c >= hi ? ["ok", "Faible"] : c >= lo ? ["warn", "Modérée"] : ["bad", "Forte"];
    cards.push(card(store, "confidence", "Pression des marchés", `<span class="light ${lvl[0]}"></span>${lvl[1]}`, k.marginal_rate_pct !== undefined ? `Taux d'emprunt ${pct(k.marginal_rate_pct)}` : "", isBase));
  }
  const svc = servicesCard(store, cur, isBase);
  if (svc) cards.push(svc);
  const co2Base = baselineFigure(b, "co2_mt")?.value;
  const decline = paramVal(store, "co2_target_decline_pct");
  if (k.co2_mt !== undefined && co2Base && decline !== undefined) {
    const target = co2Base * Math.pow(1 - decline / 100, yearOffset);
    cards.push(card(store, "co2_mt", "Climat", `${int(k.co2_mt)} Mt CO₂`, k.co2_mt <= target ? "Sur la trajectoire" : "En retard sur la trajectoire", isBase));
  }
  const sb = paramVal(store, "support_base_pct");
  const sp = paramVal(store, "support_per_capital");
  const smn = paramVal(store, "support_min_pct");
  const smx = paramVal(store, "support_max_pct");
  if (k.political_capital !== undefined && sb !== undefined && sp !== undefined && smn !== undefined && smx !== undefined) {
    const support = Math.max(smn, Math.min(smx, sb + k.political_capital * sp));
    cards.push(card(store, "political_capital", "Soutien politique", pct(support, 0), `Capital politique ${int(k.political_capital)}${k.cohesion !== undefined ? ` · cohésion ${int(k.cohesion)}` : ""}`, isBase));
  }
  return `<div class="pub">${cards.join("")}</div>`;
}

/** Bloc complet : bascule + grille. `cur` = année affichée, `prev` = précédente (pour les deltas en mode expert). */
export function kpiBlock(store: Store, cur: YearState, prev: YearState | undefined, yearOffset: number): string {
  const isBase = yearOffset === 0;
  const v = store.state.kpiView;
  return `<div class="toggle" role="group" aria-label="Niveau de lecture">
    <button type="button" data-kpiview="public" aria-pressed="${v === "public"}">Grand public</button>
    <button type="button" data-kpiview="expert" aria-pressed="${v === "expert"}">Expert</button>
  </div>
  ${v === "public" ? publicRow(store, cur, yearOffset, isBase) : expertRow(store, cur, prev, isBase)}
  <p class="hint">${isBase ? `Point de départ ${cur.year} (année de base). Appuie sur ⓘ pour la source de chaque chiffre.` : `Année ${cur.year}. ⓘ = source du point de départ ; les valeurs suivantes sont simulées.`}</p>`;
}
